import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  School,
  TrendingUp,
  ScatterPlot,
  Refresh as RefreshIcon,
  TrackChanges,
  ThumbUp,
  Compare
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea
} from 'recharts';

import { projectionsAnalysisService } from '../../../services/projectionsAnalysisService';
import AccuracyMetricsCard from './AccuracyMetricsCard';

const COLORS = {
  excellent: '#4caf50',
  good: '#ff9800',
  poor: '#f44336',
  stable: '#2196f3',
  phasing: '#9c27b0'
};

const AggregateDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  
  // Cache for EDC Schools data (using ref to avoid infinite loops)
  const cachedDataRef = useRef(null);

  const fetchAggregateData = useCallback(async (forceFetch = false) => {
    try {
      // Check if we have cached data
      if (!forceFetch && cachedDataRef.current) {
        console.log('Using cached data for EDC schools');
        setData(cachedDataRef.current);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      console.log('Fetching aggregate data for EDC schools');

      // Build params for EDC schools only
      const params = {
        scenario: 'median',
        min_years: 2
      };

      // Get team_id from localStorage if available
      const teamId = localStorage.getItem('teamId');
      console.log('Team ID from localStorage:', teamId);
      if (teamId) {
        params.team_id = teamId;
      } else {
        console.log('No team_id in localStorage, backend will use user auth to filter');
      }

      console.log('Calling API with params:', params);
      const aggregateData = await projectionsAnalysisService.getAggregateData(params);
      console.log('Received aggregate data:', aggregateData);

      if (aggregateData?.status === 'no_data') {
        setError('No backtest data available. Run backtests first.');
        setData(null);
        return;
      }

      setData(aggregateData);
      
      // Update cache
      cachedDataRef.current = aggregateData;
    } catch (err) {
      console.error('Error fetching aggregate data:', err);
      console.error('Error details:', err.response || err);
      setError(err.message || 'Failed to load aggregate data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAggregateData();
  }, [fetchAggregateData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No data available
      </Alert>
    );
  }

  // Prepare accuracy breakdown chart data
  const accuracyChartData = [
    {
      name: 'Excellent (< 10%)',
      count: data.accuracy_breakdown?.excellent?.count || 0,
      color: COLORS.excellent
    },
    {
      name: 'Good (10-25%)',
      count: data.accuracy_breakdown?.good?.count || 0,
      color: COLORS.good
    },
    {
      name: 'Needs Improvement (≥ 25%)',
      count: data.accuracy_breakdown?.poor?.count || 0,
      color: COLORS.poor
    }
  ];

  // Prepare grade stability comparison data
  const stabilityComparisonData = [
    {
      name: 'Stable Schools',
      mape: data.grade_stability_analysis?.stable?.avg_mape || 0,
      count: data.grade_stability_analysis?.stable?.count || 0
    },
    {
      name: 'Phasing In/Out',
      mape: data.grade_stability_analysis?.phasing?.avg_mape || 0,
      count: data.grade_stability_analysis?.phasing?.count || 0
    }
  ];

  const totalSchools = data.schools_analyzed || 0;
  const excellentPct = totalSchools > 0 ? Math.round((data.accuracy_breakdown?.excellent?.count || 0) / totalSchools * 100) : 0;
  const goodPct = totalSchools > 0 ? Math.round((data.accuracy_breakdown?.good?.count || 0) / totalSchools * 100) : 0;

  // Prepare accuracy decay data (MAPE by projection horizon).
  // Omit empty horizons: null MAPE with sample_size 0 is missing actuals,
  // not 0% error. Plotting those as zero made years 4–5 look perfect.
  const accuracyDecayData = [];
  for (let i = 1; i <= 5; i++) {
    const key = `${i}-year`;
    const timeframe = data.metrics_by_timeframe?.[key];
    if (timeframe && timeframe.sample_size > 0 && timeframe.mape != null) {
      accuracyDecayData.push({
        yearsAhead: i,
        mape: timeframe.mape,
        sampleSize: timeframe.sample_size
      });
    }
  }
  const omittedHorizons = [1, 2, 3, 4, 5].filter(
    (i) => !accuracyDecayData.some((d) => d.yearsAhead === i)
  );
  const horizonSampleLabel = accuracyDecayData
    .map((d) => `${d.yearsAhead}yr n=${d.sampleSize}`)
    .join(' · ');

  return (
    <Box>
      {/* Refresh Button */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            // Clear cache and force fetch
            cachedDataRef.current = null;
            fetchAggregateData(true);
          }}
          disabled={loading}
          size="medium"
        >
          Refresh Data
        </Button>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Showing EDC schools from your team assignments
        </Typography>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <AccuracyMetricsCard
            title="Within Bounds Rate"
            icon={TrackChanges}
            value={data.within_bounds_rate || 0}
            numerator={data.within_bounds_count || 0}
            denominator={data.total_projections_tested || 0}
            subtitle="Of all year projections tested"
            color={data.within_bounds_rate >= 80 ? 'success' : data.within_bounds_rate >= 60 ? 'warning' : 'error'}
            tooltipContent={
              data.within_bounds_by_stability ? (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                    Breakdown by Grade Stability:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Stable Schools:</strong> {data.within_bounds_by_stability.stable?.within_bounds || 0} / {data.within_bounds_by_stability.stable?.total || 0} 
                    ({data.within_bounds_by_stability.stable?.rate || 0}%)
                  </Typography>
                  <Typography variant="body2">
                    <strong>Phasing In/Out:</strong> {data.within_bounds_by_stability.phasing?.within_bounds || 0} / {data.within_bounds_by_stability.phasing?.total || 0} 
                    ({data.within_bounds_by_stability.phasing?.rate || 0}%)
                  </Typography>
                </Box>
              ) : null
            }
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <AccuracyMetricsCard
            title="Accurate Projections"
            icon={CheckCircle}
            value={excellentPct}
            numerator={data.accuracy_breakdown?.excellent?.count || 0}
            denominator={totalSchools}
            subtitle="Schools under 10% error"
            color="success"
            tooltipContent={
              <Box>
                <Typography variant="subtitle2" gutterBottom>Breakdown by Grade Stability:</Typography>
                <Typography variant="body2">
                  • Stable Schools: {data.accuracy_breakdown?.excellent?.stable || 0}
                </Typography>
                <Typography variant="body2">
                  • Phasing In/Out: {data.accuracy_breakdown?.excellent?.phasing || 0}
                </Typography>
              </Box>
            }
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <AccuracyMetricsCard
            title="Good Projections"
            icon={ThumbUp}
            value={goodPct}
            numerator={data.accuracy_breakdown?.good?.count || 0}
            denominator={totalSchools}
            subtitle="Schools 10-25% error"
            color="warning"
            tooltipContent={
              <Box>
                <Typography variant="subtitle2" gutterBottom>Breakdown by Grade Stability:</Typography>
                <Typography variant="body2">
                  • Stable Schools: {data.accuracy_breakdown?.good?.stable || 0}
                </Typography>
                <Typography variant="body2">
                  • Phasing In/Out: {data.accuracy_breakdown?.good?.phasing || 0}
                </Typography>
              </Box>
            }
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <AccuracyMetricsCard
            title="Median % Difference"
            icon={Compare}
            value={data.median_pct_difference || 0}
            subtitle="Midpoint vs Actual"
            suffix="%"
            color="info"
          />
        </Grid>
      </Grid>

      {/* School Accuracy Breakdown */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <CheckCircle sx={{ fontSize: 20, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                How Many Schools Have Accurate Projections?
              </Typography>
            </Box>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={accuracyChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.count}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {accuracyChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0];
                      return (
                        <Paper sx={{ p: 2, boxShadow: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {data.name}
                          </Typography>
                          <Typography variant="body2">Count: {data.value}</Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                            {((data.value / totalSchools) * 100).toFixed(0)}% of schools
                          </Typography>
                        </Paper>
                      );
                    }
                    return null;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ color: COLORS.excellent }} />
                    <Typography variant="body2">
                      <strong>{data.accuracy_breakdown?.excellent?.count || 0}</strong> Excellent
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Warning sx={{ color: COLORS.good }} />
                    <Typography variant="body2">
                      <strong>{data.accuracy_breakdown?.good?.count || 0}</strong> Good
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorIcon sx={{ color: COLORS.poor }} />
                    <Typography variant="body2">
                      <strong>{data.accuracy_breakdown?.poor?.count || 0}</strong> Needs Work
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <School sx={{ fontSize: 20, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Does Grade Stability Impact Accuracy?
              </Typography>
            </Box>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stabilityComparisonData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 13 }}
                  axisLine={{ stroke: '#e0e0e0' }}
                />
                <YAxis 
                  label={{ 
                    value: 'Average MAPE (%)', 
                    angle: -90, 
                    position: 'insideLeft',
                    style: { fontSize: 13, fill: '#64748b' }
                  }}
                  tick={{ fontSize: 12 }}
                  axisLine={{ stroke: '#e0e0e0' }}
                />
                <RechartsTooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <Paper sx={{ p: 2, boxShadow: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {data.name}
                          </Typography>
                          <Typography variant="body2">Schools: {data.count}</Typography>
                          <Typography variant="body2">Avg MAPE: {data.mape}%</Typography>
                        </Paper>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="mape" fill={COLORS.stable} radius={[4, 4, 0, 0]}>
                  {stabilityComparisonData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={index === 0 ? COLORS.stable : COLORS.phasing} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Stable Schools:</strong> {data.grade_stability_analysis?.stable?.count || 0} schools with consistent grades
                (Avg MAPE: {data.grade_stability_analysis?.stable?.avg_mape || 0}%)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Phasing In/Out:</strong> {data.grade_stability_analysis?.phasing?.count || 0} schools adding/removing grades
                (Avg MAPE: {data.grade_stability_analysis?.phasing?.avg_mape || 0}%)
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Accuracy Decay Chart */}
      {accuracyDecayData.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TrendingUp sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Projection Accuracy by Time Horizon
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This chart shows how projection accuracy decreases as we forecast further into the future. 
            <strong> Higher MAPE = Less Accurate.</strong> Green zone ({"<"}10%) = Excellent, Yellow zone (10-25%) = Good, Red zone ({">"}25%) = Needs Improvement.
            Horizons with no scored actuals are omitted, not shown as 0%.
          </Typography>
          <Box sx={{ mb: 4, p: 2, bgcolor: 'info.lighter', borderLeft: '4px solid', borderColor: 'info.main', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> While accuracy naturally decreases with longer time horizons, our quality thresholds remain constant to reflect the reliability needed for planning decisions. 
              Projections in the 'Good' zone (10-25% MAPE) provide useful directional guidance regardless of time horizon.
            </Typography>
          </Box>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={accuracyDecayData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              
              {/* Colored background zones */}
              <ReferenceArea y1={0} y2={10} fill="#22c55e" fillOpacity={0.1} />
              <ReferenceArea y1={10} y2={25} fill="#f59e0b" fillOpacity={0.1} />
              <ReferenceArea y1={25} fill="#ef4444" fillOpacity={0.1} />
              
              <XAxis 
                dataKey="yearsAhead" 
                type="number"
                domain={['dataMin', 'dataMax']}
                allowDecimals={false}
                label={{ 
                  value: 'Years Ahead', 
                  position: 'insideBottom', 
                  offset: -5,
                  style: { fontSize: 13, fill: '#64748b' }
                }}
                ticks={accuracyDecayData.map((d) => d.yearsAhead)}
                tick={{ fontSize: 13 }}
                axisLine={{ stroke: '#e0e0e0' }}
              />
              <YAxis 
                label={{ 
                  value: 'MAPE (%) - Higher = Less Accurate', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { fontSize: 13, fill: '#64748b' }
                }}
                tick={{ fontSize: 12 }}
                axisLine={{ stroke: '#e0e0e0' }}
                domain={[0, 'dataMax + 20']}
              />
              
              {/* Reference Lines for Thresholds */}
              <ReferenceLine 
                y={10} 
                stroke="#22c55e" 
                strokeWidth={2}
                strokeDasharray="5 5"
                label={{ 
                  value: 'Excellent Threshold (10%)', 
                  position: 'right', 
                  fill: '#22c55e',
                  fontSize: 11,
                  fontWeight: 600
                }}
              />
              <ReferenceLine 
                y={25} 
                stroke="#f59e0b" 
                strokeWidth={2}
                strokeDasharray="5 5"
                label={{ 
                  value: 'Good Threshold (25%)', 
                  position: 'right', 
                  fill: '#f59e0b',
                  fontSize: 11,
                  fontWeight: 600
                }}
              />
              
              <RechartsTooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    const mape = data.mape;
                    const quality = mape < 10 ? 'Excellent' : mape < 25 ? 'Good' : 'Needs Improvement';
                    const color = mape < 10 ? '#22c55e' : mape < 25 ? '#f59e0b' : '#ef4444';
                    return (
                      <Paper sx={{ p: 2, boxShadow: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {data.yearsAhead}-Year Projections
                        </Typography>
                        <Typography variant="body2">MAPE: {data.mape?.toFixed(2)}%</Typography>
                        <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
                          Quality: {quality}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                          Sample Size: {data.sampleSize}
                        </Typography>
                      </Paper>
                    );
                  }
                  return null;
                }}
              />
              <Legend wrapperStyle={{ paddingTop: '10px' }} iconType="circle" />
              
              {/* Single line with color-coded dots */}
              <Line 
                type="monotone" 
                dataKey="mape" 
                stroke="#64748b"
                strokeWidth={3}
                name="Overall MAPE"
                dot={(props) => {
                  const { cx, cy, payload, index } = props;
                  const mape = payload.mape;
                  const color = mape < 10 ? '#22c55e' : mape < 25 ? '#f59e0b' : '#ef4444';
                  return <circle key={`dot-${index}`} cx={cx} cy={cy} r={7} fill={color} stroke="#fff" strokeWidth={2} />;
                }}
                activeDot={{ r: 9 }}
              />
            </LineChart>
          </ResponsiveContainer>
          
          {horizonSampleLabel && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              Sample size by horizon: {horizonSampleLabel}
              {omittedHorizons.length > 0 && (
                <> · omitted (no actuals): {omittedHorizons.map((y) => `${y}yr`).join(', ')}</>
              )}
            </Typography>
          )}

          {/* Zone Legend */}
          <Box sx={{ mt: 2, display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 20, bgcolor: '#22c55e', opacity: 0.3, borderRadius: 1 }} />
              <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 600 }}>
                Excellent Zone ({"<"}10% MAPE)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 20, bgcolor: '#f59e0b', opacity: 0.3, borderRadius: 1 }} />
              <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 600 }}>
                Good Zone (10-25% MAPE)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 20, bgcolor: '#ef4444', opacity: 0.3, borderRadius: 1 }} />
              <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 600 }}>
                Needs Improvement ({">"}25% MAPE)
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Statistical Confidence Intervals */}
      {data.confidence_by_horizon && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TrendingUp sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Statistical Confidence Intervals
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Based on historical performance, these intervals show the expected range of error for each projection horizon.
          </Typography>
          
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Projection Horizon</TableCell>
                  <TableCell align="center">Sample Size</TableCell>
                  <TableCell align="center">Mean Error</TableCell>
                  <TableCell align="center">80% Confidence</TableCell>
                  <TableCell align="center">90% Confidence</TableCell>
                  <TableCell align="center">95% Confidence</TableCell>
                  <TableCell>Interpretation (90%)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3, 4, 5].map(yearsAhead => {
                  const ci = data.confidence_by_horizon[yearsAhead];
                  if (!ci || ci.sample_size === 0) return null;
                  
                  const isInsufficient = ci.insufficient_data || ci.sample_size < 100;
                  
                  return (
                    <TableRow key={yearsAhead} sx={{ bgcolor: isInsufficient ? 'warning.light' : 'inherit' }}>
                      <TableCell>{yearsAhead}-Year</TableCell>
                      <TableCell align="center">
                        {ci.sample_size}
                        {isInsufficient && (
                          <Chip 
                            label="Low" 
                            size="small" 
                            color="warning" 
                            sx={{ ml: 1, fontSize: '0.7rem', height: 18 }} 
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">±{ci.mean}%</TableCell>
                      <TableCell align="center">±{ci.ci_80}%</TableCell>
                      <TableCell align="center">±{ci.ci_90}%</TableCell>
                      <TableCell align="center">±{ci.ci_95}%</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {isInsufficient ? (
                            <span style={{ color: '#ffffff' }}>
                              ⚠️ Limited data - use with caution (n={ci.sample_size})
                            </span>
                          ) : (
                            `90% confident projections are within ±${ci.ci_90}% of actual`
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Actual vs Predicted Scatter Plot */}
      {data.scatter_plot_data && data.scatter_plot_data.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <ScatterPlot sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Projected vs Actual Enrollment
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Each point represents one year-projection ({data.scatter_plot_data.length.toLocaleString()} total). 
            Points on the diagonal line indicate perfect accuracy.
            Green points fell within min/max bounds, red points did not.
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                type="number" 
                dataKey="actual" 
                name="Actual" 
                label={{ 
                  value: 'Actual Enrollment', 
                  position: 'insideBottom', 
                  offset: -10,
                  style: { fontSize: 13, fill: '#64748b' }
                }}
                tick={{ fontSize: 12 }}
                axisLine={{ stroke: '#e0e0e0' }}
              />
              <YAxis 
                type="number" 
                dataKey="projected" 
                name="Projected" 
                label={{ 
                  value: 'Projected Enrollment', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { fontSize: 13, fill: '#64748b' }
                }}
                tick={{ fontSize: 12 }}
                axisLine={{ stroke: '#e0e0e0' }}
              />
              <ZAxis range={[40, 40]} />
              <RechartsTooltip
                cursor={{ strokeDasharray: '3 3' }}
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    const error = ((data.projected - data.actual) / data.actual * 100).toFixed(1);
                    return (
                      <Paper sx={{ p: 2, boxShadow: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {data.school_name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem', mb: 0.5 }}>
                          {data.school_year} ({data.years_ahead}-year projection)
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2">Projected: {data.projected.toLocaleString()}</Typography>
                        <Typography variant="body2">Actual: {data.actual.toLocaleString()}</Typography>
                        <Typography variant="body2">Error: {error}%</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, color: data.within_bounds ? 'success.main' : 'error.main', fontWeight: 500 }}>
                          {data.within_bounds ? '✓ Within bounds' : '✗ Outside bounds'}
                        </Typography>
                      </Paper>
                    );
                  }
                  return null;
                }}
              />
              <Legend wrapperStyle={{ paddingTop: '10px' }} iconType="circle" />
              {/* Draw red points first (bottom layer) */}
              <Scatter 
                name="Outside Bounds" 
                data={data.scatter_plot_data.filter(d => !d.within_bounds)} 
                fill="#f44336"
                opacity={0.6}
              />
              {/* Draw green points second (top layer) - so they're visible */}
              <Scatter 
                name="Within Bounds" 
                data={data.scatter_plot_data.filter(d => d.within_bounds)} 
                fill="#22c55e"
                opacity={0.7}
              />
              {/* Perfect accuracy line (y = x) */}
              <ReferenceLine 
                segment={[
                  { x: 0, y: 0 }, 
                  { x: Math.max(...data.scatter_plot_data.map(d => d.actual)), y: Math.max(...data.scatter_plot_data.map(d => d.actual)) }
                ]}
                stroke="#94a3b8"
                strokeDasharray="5 5"
                strokeWidth={2}
                label={{ value: 'Perfect Accuracy', position: 'insideTopRight', fill: '#64748b', fontSize: 12 }}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </Paper>
      )}
    </Box>
  );
};

export default AggregateDashboard;
